import { createContext, useCallback, useContext, useSyncExternalStore } from "react"

// ─── Store ──────────────────────────────────────────────────────────

export interface DashboardStateStore {
  getState: () => Record<string, unknown>
  getDashState: (path: string) => unknown
  setDashState: (path: string, value: unknown) => void
  initDashState: (initial: Record<string, unknown>) => void
  resetDashState: () => void
  subscribe: (listener: () => void) => () => void
  getSnapshot: () => Record<string, unknown>
}

/**
 * Adapter shape — the three calls a caller-owned store must provide.
 * `createStoreAdapter` synthesises every other DashboardStateStore
 * method on top of these so a host wiring Zustand / Redux / Jotai /
 * XState only needs to bridge their snapshot+subscribe APIs.
 */
export interface DashboardStoreAdapter {
  /** Return the current state snapshot from the underlying store. */
  getSnapshot: () => Record<string, unknown>
  /** Write a new state snapshot. */
  setSnapshot: (next: Record<string, unknown>) => void
  /** Subscribe to changes; return an unsubscribe fn. */
  subscribe: (listener: () => void) => () => void
}

/**
 * Create a lightweight store for dashboard widget state.
 * Uses JSON Pointer paths for get/set.
 */
export function createDashboardStateStore(): DashboardStateStore {
  let state: Record<string, unknown> = {}
  const listeners = new Set<() => void>()

  function notify() {
    for (const listener of listeners) listener()
  }

  function parsePath(path: string): string[] {
    if (!path || path === "/") return []
    return path
      .replace(/^\//, "")
      .split("/")
      .map((seg) => seg.replace(/~1/g, "/").replace(/~0/g, "~"))
  }

  function getDashState(path: string): unknown {
    const segments = parsePath(path)
    let current: unknown = state
    for (const seg of segments) {
      if (current && typeof current === "object" && !Array.isArray(current)) {
        current = (current as Record<string, unknown>)[seg]
      } else {
        return undefined
      }
    }
    return current
  }

  function setDashState(path: string, value: unknown): void {
    const segments = parsePath(path)
    if (segments.length === 0) {
      if (typeof value === "object" && value !== null) {
        state = { ...(value as Record<string, unknown>) }
      }
      notify()
      return
    }

    // Immutable set
    const newState = { ...state }
    let current: Record<string, unknown> = newState
    for (let i = 0; i < segments.length - 1; i++) {
      const seg = segments[i]
      const existing = current[seg]
      if (existing && typeof existing === "object" && !Array.isArray(existing)) {
        current[seg] = { ...(existing as Record<string, unknown>) }
      } else {
        current[seg] = {}
      }
      current = current[seg] as Record<string, unknown>
    }
    current[segments[segments.length - 1]] = value
    state = newState
    notify()
  }

  return {
    getState: () => state,
    getDashState,
    setDashState,
    initDashState: (initial) => {
      state = { ...initial }
      notify()
    },
    resetDashState: () => {
      state = {}
      notify()
    },
    subscribe: (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    getSnapshot: () => state,
  }
}

// ─── React Context ──────────────────────────────────────────────────

export const DashboardStateContext = createContext<DashboardStateStore | null>(null)

export function useDashboardStateStore(): DashboardStateStore {
  const store = useContext(DashboardStateContext)
  if (!store) throw new Error("useDashboardStateStore must be used within DashboardStateProvider")
  return store
}

/**
 * Subscribe to a specific state path. Re-renders only when that value changes.
 */
export function useDashState(path: string): unknown {
  const store = useDashboardStateStore()
  return useSyncExternalStore(
    store.subscribe,
    () => store.getDashState(path),
    () => store.getDashState(path)
  )
}

/**
 * Returns a getter function without subscribing (for use in expression resolver).
 */
export function useDashStateGetter(): (path: string) => unknown {
  const store = useDashboardStateStore()
  return useCallback((path: string) => store.getDashState(path), [store])
}

/**
 * Returns a setter function for updating state.
 */
export function useDashStateSetter(): (path: string, value: unknown) => void {
  const store = useDashboardStateStore()
  return useCallback((path: string, value: unknown) => store.setDashState(path, value), [store])
}

// ─── Pluggable adapters (§2.2) ──────────────────────────────────────

function parsePathSegments(path: string): string[] {
  if (!path || path === "/") return []
  return path
    .replace(/^\//, "")
    .split("/")
    .map((seg) => seg.replace(/~1/g, "/").replace(/~0/g, "~"))
}

function readByPath(snapshot: Record<string, unknown>, path: string): unknown {
  const segments = parsePathSegments(path)
  let current: unknown = snapshot
  for (const seg of segments) {
    if (current && typeof current === "object" && !Array.isArray(current)) {
      current = (current as Record<string, unknown>)[seg]
    } else {
      return undefined
    }
  }
  return current
}

function writeByPath(
  snapshot: Record<string, unknown>,
  path: string,
  value: unknown
): Record<string, unknown> {
  const segments = parsePathSegments(path)
  if (segments.length === 0) {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return { ...(value as Record<string, unknown>) }
    }
    return snapshot
  }
  const next: Record<string, unknown> = { ...snapshot }
  let cur: Record<string, unknown> = next
  for (let i = 0; i < segments.length - 1; i++) {
    const seg = segments[i]!
    const existing = cur[seg]
    if (existing && typeof existing === "object" && !Array.isArray(existing)) {
      cur[seg] = { ...(existing as Record<string, unknown>) }
    } else {
      cur[seg] = {}
    }
    cur = cur[seg] as Record<string, unknown>
  }
  cur[segments[segments.length - 1]!] = value
  return next
}

/**
 * Build a full {@link DashboardStateStore} from a minimal adapter
 * config. Mirrors `vercel-labs/json-render`'s `createStoreAdapter` —
 * lets host apps plug Redux / Zustand / Jotai / XState (or any other
 * snapshot-and-subscribe lib) into Dash's state store interface
 * without re-implementing path-based reads/writes.
 *
 * The returned store does no-op detection on set/init/reset — calls
 * with already-current values don't trigger setSnapshot, so
 * subscribers don't fire spuriously.
 */
export function createStoreAdapter(adapter: DashboardStoreAdapter): DashboardStateStore {
  const { getSnapshot, setSnapshot, subscribe } = adapter
  return {
    getState: getSnapshot,
    getSnapshot,
    subscribe,
    getDashState(path) {
      return readByPath(getSnapshot(), path)
    },
    setDashState(path, value) {
      const current = getSnapshot()
      if (readByPath(current, path) === value) return
      setSnapshot(writeByPath(current, path, value))
    },
    initDashState(initial) {
      // Replace the snapshot wholesale. Caller-owned stores typically
      // expect an immutable replacement; if the adapter wants merge
      // semantics it can implement them on top.
      setSnapshot({ ...initial })
    },
    resetDashState() {
      setSnapshot({})
    },
  }
}
